import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import './Login.css';

// @@SSO_MFA_v126@@ Email+password (with an emailed 6-digit second step for
// client logins) or Google sign-in. The Google callback hands the session back
// in the URL fragment (#gtoken=…) which this page consumes and clears.
const ERR = {
  google_no_account: 'That Google account isn’t linked to an HQ Portal user. Sign in with your email and password, or ask XYZ for an invite.',
  google_denied: 'Google sign-in was cancelled.',
  google_failed: 'Google sign-in didn’t complete. Please try again.',
  google_state: 'That sign-in attempt expired. Please try again.',
  google_off: 'Google sign-in isn’t set up yet.',
  google_link: 'Couldn’t connect Google. Sign in, then try again from the portal.',
};

export default function Login() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [googleOn, setGoogleOn] = useState(false);
  const [step, setStep] = useState('password'); // 'password' | 'code'
  const [mfaToken, setMfaToken] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(true);

  useEffect(() => {
    apiFetch('/api/auth/config')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setGoogleOn(!!(d && d.google)))
      .catch(() => setGoogleOn(false));
    const q = new URLSearchParams(window.location.search);
    if (q.get('err')) setError(ERR[q.get('err')] || 'Sign-in failed. Please try again.');
    const m = /gtoken=([^&]+)/.exec(window.location.hash || '');
    if (m) {
      window.history.replaceState(null, '', '/login');
      localStorage.setItem('hq_token', decodeURIComponent(m[1]));
      setBusy(true);
      refresh().then(() => navigate('/')).finally(() => setBusy(false));
    }
  }, []); // eslint-disable-line

  async function handleSubmit() {
    setError(''); setBusy(true);
    try {
      const res = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.error === 'google_required') {
        setError('Staff sign in with Google — use the button below.');
        return;
      }
      if (res.status === 429) { setError(data.error || 'Too many attempts. Try again in a few minutes.'); return; }
      if (!res.ok) { setError('Invalid email or password.'); return; }
      if (data.mfa_required) {
        setMfaToken(data.mfa_token); setEmailHint(data.email_hint || ''); setCode(''); setStep('code');
        return;
      }
      login(data.token, data.user);
      navigate('/');
    } catch (e) {
      setError('Could not reach the server. Try again.');
    } finally { setBusy(false); }
  }

  async function handleCode() {
    setError(''); setBusy(true);
    try {
      const res = await apiFetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: mfaToken, code, trust_device: trust }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.error === 'bad') { setError('That code isn’t right.'); return; }
        if (data.error === 'locked') { setError('Too many wrong codes. Sign in again to get a new one.'); backToPassword(); return; }
        if (res.status === 429) { setError(data.error || 'Too many attempts. Try again in a few minutes.'); return; }
        setError('That code expired. Sign in again to get a new one.'); backToPassword(); return;
      }
      login(data.token, data.user);
      navigate('/');
    } catch (e) {
      setError('Could not reach the server. Try again.');
    } finally { setBusy(false); }
  }

  function backToPassword() { setStep('password'); setMfaToken(''); setCode(''); setPassword(''); }
  function onKeyDown(e) { if (e.key === 'Enter') handleSubmit(); }
  function onCodeKeyDown(e) { if (e.key === 'Enter') handleCode(); }

  return (
    <div className="login-shell">
      <main className="login-card">
        <div className="login-brand">
          <img className="login-logo" src="/brand/xyz-mark.png" alt="XYZ Merchandise" />
          <div className="login-wordmark">XYZ <span>Merchandise</span></div>
        </div>
        <div className="login-eyebrow">HQ Portal</div>

        {step === 'code' ? (
          <>
            <p className="login-notice">
              We emailed a 6-digit code to <strong>{emailHint || 'your address'}</strong>. Enter it below.
            </p>
            <div className="login-field">
              <span className="login-ico" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="3"/><path d="M9 12h6M12 9v6"/></svg>
              </span>
              <input
                type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6}
                placeholder="6-digit code" value={code} autoFocus
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={onCodeKeyDown}
              />
            </div>
            <label className="login-check">
              <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} />
              Trust this device for 30 days
            </label>
            {error && <p className="login-error">{error}</p>}
            <button type="button" onClick={handleCode} disabled={busy || code.length !== 6}>
              {busy ? 'Checking…' : 'Verify'}
            </button>
            <div className="login-row login-row-center">
              <a href="/login" onClick={(e) => { e.preventDefault(); backToPassword(); }}>Back to sign in</a>
            </div>
          </>
        ) : (
          <>
            <div className="login-field">
              <span className="login-ico" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
              </span>
              <input
                type="email" placeholder="you@xyzmerch.com" autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={onKeyDown}
              />
            </div>

            <div className="login-field">
              <span className="login-ico" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
              </span>
              <input
                type="password" placeholder="Password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={onKeyDown}
              />
            </div>

            <div className="login-row">
              <a href="/forgot">Forgot password?</a>
            </div>

            {error && <p className="login-error">{error}</p>}

            <button type="button" onClick={handleSubmit} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            {googleOn && (
              <>
                <div className="login-divider"><span>or</span></div>
                <a className="login-google" href={'/api/auth/google' + (email ? '?hint=' + encodeURIComponent(email) : '')}>
                  Continue with Google
                </a>
                <p className="login-hint">XYZ staff: use your @xyzmerch.com Google account.</p>
              </>
            )}
          </>
        )}

        <div className="login-foot">XYZ MERCHANDISE LLC</div>
      </main>
    </div>
  );
}
